import { useTranslation } from 'next-i18next'
import { useEffect } from 'react'
import { useRouter } from 'next/router'

import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { spaceEveryThousand } from '~/bootcamp/Activities/Multiplication/MultiBy/constants'
import useRewardsStats from '~/statistics/hooks/useRewardsStats'
import { selectIsRewardsFilterSetToAllTime } from '~/statistics/redux/statistics.slice'
import { getLeagueBadgeSrcKey } from '~/statistics/utils/getLeagueBadgeSrckey'
import { getLeagueName } from '~/Leaderboard/utils/utils'
import { useGetCurrentLeagueQuery } from '~/Leaderboard/apis/leaderboard-api'
import ResponsiveImage from '~/components/Image/ResponsiveImage'
import { IUser } from '~/types/user-info-types'
import AchievementItem from '~/statistics/components/ProfileCards/AchievementItem'
import { setStatsError } from '~/familyAccount/redux/familyAccount.slice'
import ROUTES from '~/shared/constants/routes.const'

export default function RewardsList({ user }: { user?: IUser }): React.ReactElement {
  const { t } = useTranslation(['statistics', 'leaderboard'])
  const router = useRouter()
  const leagueRank = useGetCurrentLeagueQuery()
  const LeagueBadge = getLeagueBadgeSrcKey(leagueRank)
  const { xp, gems, kudos, chest, APIExecutionSteps } = useRewardsStats({ familyMemberId: user?.id })
  const dispatch = useAppDispatch()
  const { isLoading, isFetching, isUninitialized } = APIExecutionSteps
  const showLoader = isLoading || isUninitialized || isFetching

  const isRewardsFilterSetToAllTime = useAppSelector(selectIsRewardsFilterSetToAllTime)
  const funFactChestResult = isRewardsFilterSetToAllTime
    ? `${chest?.opened || 0} / ${chest?.total || 0}`
    : `${chest?.opened || 0}`

  const leagueName = getLeagueName(LeagueBadge?.highestLeagueNumber || 0)

  useEffect(() => {
    if (router.asPath.includes('family') && APIExecutionSteps?.isError) {
      // Safely handle error property if it exists
      const errorData = (APIExecutionSteps as any)?.error?.data?.error
      if (errorData) {
        dispatch(setStatsError(errorData))
        router.push(ROUTES.FAMILY_ADMIN_PANEL)
      }
    }
  }, [APIExecutionSteps, dispatch, router])

  return (
    <div className="space-y-3">
      <AchievementItem
        height={22}
        imgAlt={t('statistics:alt_texts.xp')}
        imgSrcKey="scoreboard/xp.png"
        isLoading={showLoader}
        result={xp}
        title={t('statistics:xp-earned')}
        width={24}
      />
      <AchievementItem
        height={24}
        imgAlt={t('statistics:alt_texts.kudos')}
        imgSrcKey="bootcamp/kudos-orb.png"
        isLoading={showLoader}
        result={kudos}
        title={t('statistics:kudos-earned')}
        width={24}
      />
      <AchievementItem
        height={18}
        imgAlt={t('statistics:alt_texts.gems')}
        imgSrcKey="icons/gem-icon.png"
        isLoading={showLoader}
        result={
          <div className="flex items-center space-x-1">
            <p>{spaceEveryThousand(gems || 0)}</p>
          </div>
        }
        title={t('statistics:gems')}
        width={24}
      />

      <AchievementItem
        height={18}
        imgAlt={t('statistics:alt_texts.chest')}
        imgSrcKey="icons/fun-fact-chest-v2.png"
        isLoading={showLoader}
        result={funFactChestResult}
        title={t('statistics:fun-fact-chests')}
        width={24}
      />

      <AchievementItem
        height={19}
        imgAlt={t('statistics:alt_texts.league')}
        imgSrcKey="icons/leaderboard-icon.png"
        isLoading={showLoader}
        result={
          <div className="flex items-center gap-1">
            <p className="font-normal">{t(`leaderboard:badges.${leagueName}`)}</p>
            {LeagueBadge && (
              <ResponsiveImage
                alt={t(`leaderboard:badges.${leagueName}`)}
                height={25}
                srcKey={LeagueBadge.highestLeague}
                width={19}
              />
            )}
          </div>
        }
        title={t('statistics:highestLeague')}
        width={24}
      />
    </div>
  )
}
